import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core'
import { LiveVideoComponent } from './live-video/live-video.component'
import { BettingSectionComponent } from './betting-section/betting-section.component'
import { CardDetail } from '@core/interfaces'
import { ActivatedRoute } from '@angular/router'
import { AccountService, WebSocketService } from '@core/services'
import { GameHttp, UserAccountHttp } from '@core/https'
import { ModeEnum } from '@core/enums'
import { CardTypeEnum } from '@shared/enums'
import { BetResultDto, BuyInUpdateDto } from '@core/dtos'
import { PickedCardComponent, TimerV2Component } from '@shared/components'
import { GameHistoryComponent } from './game-history/game-history.component'
import { GameHistoryMobileComponent } from './game-history-mobile/game-history-mobile.component'
import { PlayerService } from '@shared/services'
import { HeaderComponent } from '../header/header.component'
import { CommonModule } from '@angular/common'
import { DomSanitizer } from '@angular/platform-browser'
import { ClassicService } from '@rush-shared/services'

@Component({
  selector: 'app-inbetween-app',
  standalone: true,
  imports: [
    LiveVideoComponent,
    BettingSectionComponent,
    TimerV2Component,
    PickedCardComponent,
    GameHistoryComponent,
    GameHistoryMobileComponent,
    HeaderComponent,
    CommonModule,
  ],
  providers: [WebSocketService],
  templateUrl: './inbetween-app.component.html',
  styleUrl: './inbetween-app.component.sass',
})
export class InbetweenAppComponent implements OnInit, OnDestroy, AfterViewInit {
  CardTypeEnum = CardTypeEnum

  private get playerId(): string {
    return this._accountService.currentUserId
  }

  get authenticated() {
    return this._accountService.authenticated
  }

  dealerId: string
  countdown: number
  playerCards: CardDetail[]
  showMaverickCard: boolean
  showPlayerCards: boolean
  maverickCard: CardDetail[]
  tableName: string

  constructor(
    private _playerService: PlayerService,
    private _activatedroute: ActivatedRoute,
    private _webSocketService: WebSocketService,
    private _userAccountHttp: UserAccountHttp,
    private _gameHttp: GameHttp,
    private _accountService: AccountService,
    private _classicService: ClassicService,
    public _sanitizer: DomSanitizer,
  ) {}

  ngAfterViewInit(): void {
    this._gameHttp.getInitData(this.dealerId, this.playerId).subscribe({
      next: (i) => {
        this.tableName = i.tableName
        this._playerService.potBonus = i.pot?.amount || 0
        this._playerService.streamUrl = this._sanitizer.bypassSecurityTrustResourceUrl(i.streamUrl)

        if (i.seat) {
          this._playerService.setSeat(i.seat)
        }

        if (i.playerBet) {
          this._playerService.playerBet = i.playerBet
        }

        this._playerService.isBetLock = i.playerBet?.isLocked === true
        this._playerService.minBet = i.minBet
        this._playerService.maxBet = i.maxBet
        this._playerService.foldCost = i.foldCost
        this._playerService.buyInCost = i.buyInCost
        this._playerService.gameHistory = i.gameHistory
        this._playerService.currentSeatNoFromDealer = i.gameConfig.seatNo

        this._classicService.setMode(i.gameConfig.mode)
        this._playerService.allowBuyIn =
          i.gameConfig.mode === ModeEnum.BuyInOpen && !i.seat ? true : false

        if (i.gameConfig.firstCard && i.gameConfig.secondCard) {
          this.playerCards = [i.gameConfig.firstCard, i.gameConfig.secondCard]
          if (i.gameConfig.mode === ModeEnum.PlayerBets) {
            this.showPlayerCards = true
          }
        }

        if (i.gameConfig.maverickCard && this._classicService.mode$.value === ModeEnum.Result) {
          this.maverickCard = [i.gameConfig.maverickCard]
          if (i.gameConfig.mode === ModeEnum.Result) {
            this.showMaverickCard = true
          }
        }
        if (this.playerCards && this._classicService.mode$.value === ModeEnum.PlayerBets) {
          const [firstCard, secondCard] = this.playerCards
          if (firstCard.number === secondCard.number) {
            this._playerService.isPair = true
          } else {
            this._playerService.isPair = false
          }
        }

        this._playerService.invalidDealer = false
        this.listenToSocket()
      },
      error: (err) => {
        this._playerService.invalidDealer = true
      },
    })
  }

  ngOnInit(): void {
    this.dealerId = this._activatedroute.snapshot.paramMap.get('dealerId') as string
    this._playerService.setActiveDealerById(this.dealerId)

    this._gameHttp.getDealerTableInfo(this.dealerId).subscribe((tableInfo) => {
      this._playerService.tableInfo = tableInfo
    })

    this._userAccountHttp.getUserById(this.playerId).subscribe((user) => {
      this._playerService.setActivePlayer({
        id: user.id,
        name: user.fullName,
        chips: user?.chips || 0,
      })
    })
  }

  ngOnDestroy(): void {
    this._playerService.clearBetResult()
    this._playerService.clearCurrentSeat()
    this._webSocketService.closeAllListeners()
  }

  listenToSocket() {
    this._playerService.currentSeat$.subscribe((seat) => {
      const seatNo = seat?.seatNo || 0

      if (seatNo) {
        this._webSocketService.listenToBetResult(this.playerId, (result: BetResultDto) => {
          this._playerService.setBetResult({
            betTypeId: result.result,
            chips: result.currentChips,
            isWinner: result.isWinner,
            playerId: result.playerId,
            earnedChips: result.earnedChips,
            seatNo,
          })
        })

        this._webSocketService.listenToMaxBet(this.dealerId, seatNo, ({ maxBet }) => {
          this._playerService.maxBet = maxBet
        })
      }
    })

    this._webSocketService.listenToBuyInLocked(this.playerId, (buyInData: BuyInUpdateDto) => {
      this._playerService.allowBuyIn = !buyInData.locked
      this._playerService.setSeat(buyInData.seat)
    })

    this._webSocketService.listenToPlayerCardsConfirmation(this.dealerId, ({ isConfirm }) => {
      if (isConfirm) {
        this.showPlayerCards = true
        this._playerService.isBetLock = false
      }

      const [firstCard, secondCard] = this.playerCards
      if (this.playerCards.length > 0) {
        if (firstCard.number === secondCard.number) {
          this._playerService.isPair = true
        } else {
          this._playerService.isPair = false
        }
      }
    })

    this._webSocketService.listenToIncommingChips(this.playerId, (incomming) => {
      this._playerService.incommingChips = { ...incomming, show: true }

      if (incomming.useEarnClass && this._playerService.activePlayer) {
        this._playerService.activePlayer.chips += incomming.chips
      } else {
        this._playerService.activePlayer.chips -= incomming.chips
      }
    })

    this._webSocketService.listenToMaverickCardsConfirmation(this.dealerId, ({ isConfirm }) => {
      if (isConfirm) {
        this.showMaverickCard = true
      }
    })

    this._webSocketService.listenToPlayerCards(this.dealerId, (cards) => {
      this.playerCards = cards
    })

    this._webSocketService.listenToMaverickCards(this.dealerId, (cards: CardDetail[]) => {
      this.maverickCard = cards
    })

    this._webSocketService.listenToCountdown(this.dealerId, (timeRemaining: number) => {
      this.countDownCallback(timeRemaining)

      if (this._classicService.mode$.value === ModeEnum.Result && timeRemaining < 1) {
        this._playerService.clearBetResult()
      }
    })

    this._webSocketService.listenToMode(this.dealerId, ({ mode, currentSeatNo }) => {
      this._classicService.setMode(mode)
      this._playerService.currentSeatNoFromDealer = currentSeatNo

      this._playerService.allowBuyIn = mode === ModeEnum.BuyInOpen ? true : false

      if (mode === ModeEnum.Setup) {
        this._playerService.clearCurrentSeat()
      }
    })

    this._webSocketService.listenToPotBonusUpdate(this.dealerId, (pot) => {
      this._playerService.potBonus = pot.amount
    })

    this._webSocketService.listenToPlayerBetUpdate(this.playerId, (i) => {
      this._playerService.playerBet = i.playerBet
    })

    this._webSocketService.listenToGameHistory(this.dealerId, (gameHistory) => {
      this._playerService.gameHistory = gameHistory
    })
  }

  private resetState() {
    this.playerCards = []
    this.maverickCard = []
    this.showPlayerCards = false
    this.showMaverickCard = false
    this._playerService.isPair = false

    if (this._playerService.playerBet) {
      this._playerService.playerBet.chips = 0
      this._playerService.playerBet.isLocked = false
    }
  }

  private countDownCallback(timeRemaining: number) {
    this.countdown = timeRemaining
    this._classicService.setCountdown(timeRemaining)

    switch (this._classicService.mode$.value) {
      case ModeEnum.PlayerBets:
        this.showPlayerCards = true
        break
      case ModeEnum.Result:
        this.showPlayerCards = true
        this.showMaverickCard = true
        if (timeRemaining === 0) {
          this.resetState()
        }
        break
      case ModeEnum.MaverickCard:
        if (this._playerService.playerBet.chips > 0 && !this._playerService.isBetLock) {
          this._playerService.initializeBet()
        }
        break
      default:
        break
    }
  }
}
